import type { Action, Cell, PlayerId, Pos } from '../protocol-types'

/**
 * 座位 → 颜色类名与形状。形状和颜色同时区分，色弱玩家不靠颜色也能分辨。
 *
 * `bg` 和 `text` **必须各写一份完整的类名，不能从对方替换前缀得来**。
 * Tailwind 是扫源码里的字面量来决定生成哪些 CSS 的：用前缀替换拼出来的
 * `text-seat0` 在源码里根本不存在，于是那条 CSS 规则不会被生成，
 * 棋子就变成没有颜色的字符 —— 这个 bug 真实发生过，界面上三家棋子全是灰白色。
 * tests/tailwind-classnames.test.ts 钉住了这条。
 */
const SEAT_STYLE: Record<PlayerId, { bg: string; text: string; glyph: string; label: string }> = {
  0: { bg: 'bg-seat0', text: 'text-seat0', glyph: '▲', label: '蓝' },
  1: { bg: 'bg-seat1', text: 'text-seat1', glyph: '●', label: '红' },
  2: { bg: 'bg-seat2', text: 'text-seat2', glyph: '■', label: '绿' },
}

export function seatStyle(seat: PlayerId) {
  return SEAT_STYLE[seat]
}

const posKey = (p: Pos) => `${p.row},${p.col}`

interface BoardProps {
  board: Cell[][]
  /** 轮到自己时服务端给的合法行动。可点性一律由它决定。 */
  legalActions: Action[]
  /** 当前选中待确认的格子；null 表示还没选 */
  selected: Pos | null
  /** 玩家此刻打算做什么 —— 决定哪些格子算可点 */
  mode: 'place' | 'block'
  onSelect(pos: Pos): void
}

/**
 * 棋盘。正方形、占满宽度 —— 这是玩家全程盯着的东西。
 *
 * 尺寸完全来自 `board.length`，不写死 6：房主可以配成 5 或 7。
 * 用 CSS Grid 而不是逐格算像素，因为格子边长得随屏幕宽度浮动，
 * 而 `aspect-square` 能保证它始终是正方形。
 */
export function Board({ board, legalActions, selected, mode, onSelect }: BoardProps) {
  const size = board.length

  // 把合法行动摊成一个坐标集合，渲染时 O(1) 查。
  // 只收当前 mode 的那类动作 —— 想落子时不该把「可放禁区」的格子也点亮。
  const playable = new Set(
    legalActions.filter((a) => a.type === mode).map((a) => posKey((a as { pos: Pos }).pos)),
  )

  return (
    <div
      className="no-select grid w-full gap-[2px] rounded-lg bg-slate-700 p-[2px]"
      style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`, aspectRatio: '1 / 1' }}
      role="grid"
      aria-label={`${size} 乘 ${size} 棋盘`}
    >
      {board.map((row, r) =>
        row.map((cell, c) => {
          const pos = { row: r, col: c }
          const key = posKey(pos)
          const canTap = playable.has(key)
          const isSelected = selected !== null && posKey(selected) === key

          return (
            <button
              key={key}
              type="button"
              role="gridcell"
              // 不可点的格子用 disabled，而不是「点了没反应」——
              // 后者会让玩家反复戳同一个地方，以为是自己没点准。
              disabled={!canTap}
              onClick={() => onSelect(pos)}
              aria-label={cellLabel(cell, pos, canTap)}
              className={[
                // 触控区靠 aspect-square + 整体占满宽度保证：
                // 375px 屏宽下 6 格约 60px，7 格约 51px，都在 44px 之上。
                'flex aspect-square items-center justify-center rounded text-lg font-bold transition-colors',
                cell.kind === 'empty' ? 'bg-slate-800' : 'bg-slate-800/60',
                canTap ? 'ring-1 ring-inset ring-amber-400/60' : '',
                isSelected ? 'ring-4 ring-inset ring-amber-300 bg-amber-500/25' : '',
              ].join(' ')}
            >
              <CellFace cell={cell} />
            </button>
          )
        }),
      )}
    </div>
  )
}

function CellFace({ cell }: { cell: Cell }) {
  if (cell.kind === 'stone') {
    const s = SEAT_STYLE[cell.owner]
    return <span className={`text-xl ${s.text}`}>{s.glyph}</span>
  }
  if (cell.kind === 'block') {
    return <span className="text-slate-500">✕</span>
  }
  return null
}

/** 给读屏与测试用的可读描述。不依赖颜色，因为颜色不是信息载体。 */
function cellLabel(cell: Cell, pos: Pos, canTap: boolean): string {
  const where = `第 ${pos.row + 1} 行第 ${pos.col + 1} 列`
  if (cell.kind === 'stone') return `${where}，${SEAT_STYLE[cell.owner].label}方棋子`
  if (cell.kind === 'block') return `${where}，禁区`
  return canTap ? `${where}，空格，可落子` : `${where}，空格`
}
